import React, { useCallback, useMemo } from "react";
import { AuditLogChangeType } from "@common/types/auditLog";
import { Picker } from "@design-system/Picker/Picker.lib";
import { FilterActionsFooter } from "@design-system/Filter/FilterActionsFooter/FilterActionsFooter";
import type { ActivityFilterRows } from "@components/activityTimeline/filters/activityFilters.types";
import {
    HistoryChangeFilters,
    RevertedFilterValue,
} from "@hooks/view/useHistoryPageFiltersCache";
import {
    buildActivityFilters,
    getActivitiesFromValues,
} from "./historyActivityFilters";

interface ActionOption {
    label: string;
    changeType: AuditLogChangeType | null;
}

const REVERTED_OPTION: ActionOption = {
    label: "Reverted",
    changeType: null,
};

const CHANGE_TYPE_OPTIONS: ActionOption[] = [
    { label: "Created", changeType: AuditLogChangeType.CREATE },
    { label: "Updated", changeType: AuditLogChangeType.UPDATE },
    { label: "Deleted", changeType: AuditLogChangeType.DELETE },
];

interface Props {
    filters: HistoryChangeFilters;
    onChange: (filters: HistoryChangeFilters) => void;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

const isRevertedOption = (option: ActionOption): boolean =>
    option.changeType === null;

const ACTION_OPTIONS: ActionOption[] = [
    ...CHANGE_TYPE_OPTIONS,
    REVERTED_OPTION,
];

const ACTION_OPTIONS_BY_LABEL = new Map(
    ACTION_OPTIONS.map((option) => [option.label, option]),
);

const ACTION_ITEMS = ACTION_OPTIONS.map((option) => ({
    value: option.label,
    label: option.label,
}));

/** Action: the change types, plus Reverted. */
export function useActionFilterRows({
    filters,
    onChange,
}: Omit<Props, "open" | "onOpenChange">): ActivityFilterRows<string> {
    const isRevertedSelected =
        filters.revertedFilter === RevertedFilterValue.REVERTED ||
        filters.revertedFilter === RevertedFilterValue.ALL;

    const isOptionSelected = useCallback(
        (option: ActionOption): boolean => {
            if (isRevertedOption(option)) {
                return isRevertedSelected;
            }
            return option.changeType !== null
                ? (filters.changeTypes?.includes(option.changeType) ?? false)
                : false;
        },
        [filters.changeTypes, isRevertedSelected],
    );

    const selectedOptions = useMemo(
        () => ACTION_OPTIONS.filter(isOptionSelected),
        [isOptionSelected],
    );

    const selectedCount = selectedOptions.length;
    const selectedLabels = useMemo(
        () => selectedOptions.map((option) => option.label),
        [selectedOptions],
    );

    const applySelection = useCallback(
        (selectedActionOptions: ActionOption[]) => {
            const changeTypes: AuditLogChangeType[] = [];
            for (const option of selectedActionOptions) {
                if (option.changeType !== null) {
                    changeTypes.push(option.changeType);
                }
            }

            const isRevertedSelectedNext =
                selectedActionOptions.some(isRevertedOption);

            let revertedFilter: RevertedFilterValue | undefined;
            if (isRevertedSelectedNext) {
                revertedFilter =
                    changeTypes.length > 0
                        ? RevertedFilterValue.ALL
                        : RevertedFilterValue.REVERTED;
            }

            const activities = getActivitiesFromValues(filters.activityValues);

            onChange({
                changeTypes: changeTypes.length > 0 ? changeTypes : undefined,
                activityFilters: buildActivityFilters(activities, changeTypes),
                revertedFilter,
            });
        },
        [filters.activityValues, onChange],
    );

    const resetFilter = useCallback(() => {
        const activities = getActivitiesFromValues(filters.activityValues);

        onChange({
            changeTypes: undefined,
            activityFilters: buildActivityFilters(activities, undefined),
            revertedFilter: undefined,
        });
    }, [filters.activityValues, onChange]);

    let chipLabel: string | undefined;
    if (selectedCount > 0) {
        chipLabel =
            selectedCount === 1
                ? selectedLabels[0]
                : `${selectedCount} actions`;
    }

    return {
        list: { items: ACTION_ITEMS },
        value: selectedLabels,
        onValueChange: (labels) =>
            applySelection(
                labels.flatMap((label) => {
                    const option = ACTION_OPTIONS_BY_LABEL.get(label);
                    return option ? [option] : [];
                }),
            ),
        onClear: resetFilter,
        chipLabel,
    };
}

export const ActivityFilterCombobox: React.FC<Props> = ({
    open,
    onOpenChange,
    ...props
}) => {
    const rows = useActionFilterRows(props);
    const selectedCount = rows.value.length;

    return (
        <Picker.Select
            {...rows.list}
            multiple
            value={rows.value}
            open={open}
            onOpenChange={onOpenChange}
            onValueChange={rows.onValueChange}
            placeholder="All actions"
            renderValue={() => rows.chipLabel}
        >
            {selectedCount > 0 && (
                <Picker.Footer>
                    <FilterActionsFooter
                        deselectFunction={rows.onClear}
                        selectedCount={selectedCount}
                        hideBorderAndPadding
                    />
                </Picker.Footer>
            )}
        </Picker.Select>
    );
};
